import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share2, MessageCircle, Instagram, Twitter, Facebook, Check, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import html2canvas from "html2canvas";

const API = process.env.REACT_APP_BACKEND_URL;

// Mandala SVG Pattern for badges
const MandalaPattern = ({ color, size = 200 }) => {
  const colors = {
    bronze: { primary: "#CD7F32", secondary: "#8B4513", glow: "rgba(205, 127, 50, 0.5)" },
    silver: { primary: "#C0C0C0", secondary: "#808080", glow: "rgba(192, 192, 192, 0.5)" },
    gold: { primary: "#FFD700", secondary: "#DAA520", glow: "rgba(255, 215, 0, 0.6)" }
  };
  
  const { primary, secondary, glow } = colors[color] || colors.gold;
  
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" className="mandala-pattern">
      <defs>
        <radialGradient id={`mandala-gradient-${color}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={primary} stopOpacity="1" />
          <stop offset="70%" stopColor={secondary} stopOpacity="0.8" />
          <stop offset="100%" stopColor={secondary} stopOpacity="0.3" />
        </radialGradient>
        <filter id={`mandala-glow-${color}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Outer glow circle */}
      <circle cx="100" cy="100" r="95" fill="none" stroke={glow} strokeWidth="2" opacity="0.5" />
      
      {/* Main mandala circles */}
      <g filter={`url(#mandala-glow-${color})`}>
        {/* Outer petals */}
        {[...Array(12)].map((_, i) => (
          <ellipse
            key={`outer-${i}`}
            cx="100"
            cy="30"
            rx="15"
            ry="25"
            fill="none"
            stroke={primary}
            strokeWidth="1.5"
            transform={`rotate(${i * 30} 100 100)`}
            opacity="0.8"
          />
        ))}
        
        {/* Middle petals */}
        {[...Array(8)].map((_, i) => (
          <ellipse
            key={`middle-${i}`}
            cx="100"
            cy="50"
            rx="12"
            ry="20"
            fill="none"
            stroke={primary}
            strokeWidth="1.5"
            transform={`rotate(${i * 45 + 22.5} 100 100)`}
            opacity="0.9"
          />
        ))}
        
        {/* Inner petals */}
        {[...Array(6)].map((_, i) => (
          <ellipse
            key={`inner-${i}`}
            cx="100"
            cy="70"
            rx="10"
            ry="15"
            fill={`url(#mandala-gradient-${color})`}
            stroke={primary}
            strokeWidth="1"
            transform={`rotate(${i * 60} 100 100)`}
            opacity="0.7"
          />
        ))}
        
        {/* Center lotus */}
        <circle cx="100" cy="100" r="25" fill={`url(#mandala-gradient-${color})`} stroke={primary} strokeWidth="2" />
        <circle cx="100" cy="100" r="15" fill="none" stroke={secondary} strokeWidth="1.5" opacity="0.8" />
        <circle cx="100" cy="100" r="8" fill={primary} />
        
        {/* Decorative dots */}
        {[...Array(12)].map((_, i) => (
          <circle
            key={`dot-${i}`}
            cx={100 + 40 * Math.cos((i * 30 * Math.PI) / 180)}
            cy={100 + 40 * Math.sin((i * 30 * Math.PI) / 180)}
            r="3"
            fill={primary}
          />
        ))}
      </g>
    </svg>
  );
};

// Shareable Badge Card Component
const ShareableBadge = React.forwardRef(({ celebration, className }, ref) => {
  const bgColors = {
    bronze: "from-amber-900/90 via-amber-800/80 to-stone-900/90",
    silver: "from-slate-800/90 via-gray-700/80 to-slate-900/90",
    gold: "from-amber-800/90 via-yellow-700/80 to-amber-900/90"
  };
  
  return (
    <div 
      ref={ref}
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{ width: "400px", height: "500px" }}
    >
      {/* Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${bgColors[celebration.badge_color]} backdrop-blur-xl`} />
      
      {/* Stars/particles background */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>
      
      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full p-6 text-center">
        {/* Om symbol */}
        <div className="text-3xl mb-2 opacity-60">ॐ</div>
        
        {/* Mandala Badge */}
        <div className="relative mb-4">
          <MandalaPattern color={celebration.badge_color} size={160} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl font-bold" style={{ color: celebration.badge_color === 'gold' ? '#FFD700' : celebration.badge_color === 'silver' ? '#C0C0C0' : '#CD7F32' }}>
              {celebration.milestone}
            </span>
          </div>
        </div>
        
        {/* Title */}
        <h2 className="font-serif text-3xl text-white mb-1">{celebration.title}</h2>
        <p className="text-lg text-white/70 mb-1">{celebration.subtitle}</p>
        <p className="text-2xl text-amber-400 font-serif mb-4">{celebration.sanskrit}</p>
        
        {/* Days text */}
        <div className="bg-black/30 rounded-full px-6 py-2 mb-4">
          <span className="text-white font-medium">{celebration.milestone} Days of Practice</span>
        </div>
        
        {/* User name */}
        <p className="text-white/80 text-sm mb-2">Achieved by</p>
        <p className="text-white font-medium text-lg">{celebration.user_name}</p>
        
        {/* Brand */}
        <div className="absolute bottom-4 left-0 right-0 text-center">
          <p className="text-white/50 text-xs">Maya Manthan • Inner Transformation Platform</p>
        </div>
      </div>
    </div>
  );
});

ShareableBadge.displayName = 'ShareableBadge';

export default function StreakCelebrationModal({ isOpen, onClose, celebration, onCelebrated }) {
  const [isSharing, setIsSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const badgeRef = useRef(null);
  
  const markAsCelebrated = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API}/api/maya-manthan/streak/celebrate?milestone=${celebration.milestone}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (onCelebrated) onCelebrated();
    } catch (error) {
      console.error("Error marking celebration:", error);
    }
  };
  
  const handleClose = () => {
    markAsCelebrated();
    onClose();
  };
  
  const downloadBadge = async () => {
    if (!badgeRef.current) return;
    
    setIsSharing(true);
    try {
      const canvas = await html2canvas(badgeRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `maya-manthan-${celebration.title.toLowerCase()}-badge.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (error) {
      console.error("Error downloading badge:", error);
    } finally {
      setIsSharing(false);
    }
  };
  
  const shareToWhatsApp = () => {
    const text = encodeURIComponent(celebration.share_text);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    markAsCelebrated();
  };
  
  const shareToInstagram = async () => {
    // Instagram doesn't have direct share API, so we download the image
    await downloadBadge();
    alert("Badge downloaded! Open Instagram and share it to your story.");
    markAsCelebrated();
  };
  
  const shareToTwitter = () => {
    const text = encodeURIComponent(celebration.share_text);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    markAsCelebrated();
  };
  
  const shareToFacebook = () => {
    const text = encodeURIComponent(celebration.share_text);
    window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank');
    markAsCelebrated();
  };
  
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${celebration.title} - Maya Manthan`,
          text: celebration.share_text,
          url: window.location.origin
        });
        markAsCelebrated();
      } catch (error) {
        console.log("Share cancelled");
      }
    } else {
      // Fallback to copy
      navigator.clipboard.writeText(celebration.share_text);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    }
  };
  
  if (!isOpen || !celebration) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative max-w-lg w-full bg-gradient-to-br from-[#0d0d2b] via-[#1a1a3a] to-[#0d0d2b] rounded-3xl overflow-hidden border border-white/10"
          onClick={(e) => e.stopPropagation()}
          data-testid="streak-celebration-modal"
        >
          {/* Animated particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-amber-400/40 rounded-full"
                initial={{ 
                  x: Math.random() * 500 - 250,
                  y: 600,
                  opacity: 0 
                }}
                animate={{ 
                  y: -100,
                  opacity: [0, 1, 0],
                  scale: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 3
                }}
                style={{ left: `${Math.random() * 100}%` }}
              />
            ))}
          </div>
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
            data-testid="close-celebration-modal"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          
          {/* Content */}
          <div className="relative z-10 p-8 text-center">
            {/* Sparkles header */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="flex justify-center mb-4"
            >
              <Sparkles className="w-8 h-8 text-amber-400" />
            </motion.div>
            
            {/* Congratulations */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-amber-400 text-sm tracking-widest uppercase mb-2"
            >
              Congratulations {celebration.user_name}!
            </motion.p>
            
            {/* Badge Preview */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, type: "spring", damping: 15 }}
              className="flex justify-center mb-6"
            >
              <div className="relative">
                <MandalaPattern color={celebration.badge_color} size={140} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">{celebration.milestone}</span>
                </div>
              </div>
            </motion.div>
            
            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="font-serif text-3xl text-white mb-1"
            >
              {celebration.title}
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-white/70 mb-1"
            >
              {celebration.subtitle}
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-2xl text-amber-400 font-serif mb-4"
            >
              {celebration.sanskrit}
            </motion.p>
            
            {/* Message */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-white/80 text-sm leading-relaxed mb-2 max-w-sm mx-auto"
            >
              {celebration.message}
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-amber-400/80 text-sm italic mb-6"
            >
              "{celebration.encouragement}"
            </motion.p>
            
            {/* Share Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="space-y-4"
            >
              <p className="text-white/60 text-sm">Share your achievement</p>
              
              {/* Social Share Buttons */}
              <div className="flex justify-center gap-3 mb-4">
                <button
                  onClick={shareToWhatsApp}
                  className="w-12 h-12 rounded-full bg-green-600/20 hover:bg-green-600/40 flex items-center justify-center transition-colors"
                  title="Share to WhatsApp"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle className="w-5 h-5 text-green-400" />
                </button>
                
                <button
                  onClick={shareToInstagram}
                  className="w-12 h-12 rounded-full bg-pink-600/20 hover:bg-pink-600/40 flex items-center justify-center transition-colors"
                  title="Share to Instagram"
                  data-testid="share-instagram"
                >
                  <Instagram className="w-5 h-5 text-pink-400" />
                </button>
                
                <button
                  onClick={shareToTwitter}
                  className="w-12 h-12 rounded-full bg-blue-500/20 hover:bg-blue-500/40 flex items-center justify-center transition-colors"
                  title="Share to Twitter/X"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-5 h-5 text-blue-400" />
                </button>
                
                <button
                  onClick={shareToFacebook}
                  className="w-12 h-12 rounded-full bg-blue-600/20 hover:bg-blue-600/40 flex items-center justify-center transition-colors"
                  title="Share to Facebook"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-5 h-5 text-blue-500" />
                </button>
              </div>
              
              {/* Download & Share buttons */}
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={downloadBadge}
                  disabled={isSharing}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  data-testid="download-badge-btn"
                >
                  {isSharing ? (
                    <span className="animate-spin mr-2">⏳</span>
                  ) : shareSuccess ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {shareSuccess ? "Downloaded!" : "Download Badge"}
                </Button>
                
                <Button
                  onClick={shareNative}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="share-native-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </motion.div>
            
            {/* Continue button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="mt-6"
            >
              <button
                onClick={handleClose}
                className="text-white/50 hover:text-white/80 text-sm underline transition-colors"
              >
                Continue my practice
              </button>
            </motion.div>
          </div>
          
          {/* Hidden shareable badge for download */}
          <div className="absolute -left-[9999px] top-0">
            <ShareableBadge ref={badgeRef} celebration={celebration} />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
